import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  infoLight: '#e3f2fd'
};

// Options pour les profiles
const PROFILE_OPTIONS = [
  { value: "", label: "Tous les profiles" },
  { value: "2", label: "Contrôleur", icon: "bi-person-check" },
  { value: "3", label: "Gestionnaire", icon: "bi-person-gear" },
  { value: "4", label: "Agent", icon: "bi-person-video" }
];

const getProfileInfo = (profile) => {
  const found = PROFILE_OPTIONS.find(opt => opt.value === profile);
  return found || { label: profile, icon: "bi-person" };
};

const getProfileColor = (profile) => {
  switch(profile) {
    case "2": return "#1976d2"; // Bleu pour Contrôleur
    case "3": return "#388e3c"; // Vert pour Gestionnaire
    case "4": return "#f57c00"; // Orange pour Agent
    default: return "#757575";
  }
};

const formatDuration = (seconds) => {
  if (!seconds) return "00:00";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

// Composant VideoPlayer identique à TutorialAdminList
const VideoPlayer = ({ videoUrl, duration, videoId, onPlay, onPause, isPlaying, categoryColor }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', updateTime);
    return () => video.removeEventListener('timeupdate', updateTime);
  }, []);

  const handleError = () => {
    console.error(`Erreur de chargement de la vidéo: ${videoUrl}`);
    setHasError(true);
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  const formatCurrentTime = () => {
    return formatDuration(currentTime);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().then(() => {
        onPlay(videoId);
      }).catch(error => {
        console.error("Erreur de lecture:", error);
        setHasError(true);
      });
    } else {
      video.pause();
      onPause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    if (!video || duration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percent = clickX / width;
    const newTime = percent * duration;

    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  if (hasError) {
    return (
      <div className="video-error-container" style={{
        backgroundColor: '#1a1a1a',
        aspectRatio: '16/9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff'
      }}>
        <i className="bi bi-exclamation-triangle fs-1 mb-3" style={{ color: '#ff4444' }}></i>
        <span className="mb-2">Erreur de chargement de la vidéo</span>
        <small className="text-muted">URL: {videoUrl?.substring(0, 50)}...</small>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="video-player-container"
      style={{
        position: 'relative',
        backgroundColor: '#000',
        aspectRatio: '16/9',
        overflow: 'hidden',
        cursor: 'pointer'
      }}
      onMouseEnter={() => {
        setIsHovering(true);
        setShowControls(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
        if (!isPlaying) {
          setShowControls(false);
        }
      }}
      onClick={(e) => {
        if (!e.target.closest('.video-controls')) {
          togglePlay();
        }
      }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000'
        }}
        preload="metadata"
        onError={handleError}
        playsInline
      />

      {!isPlaying && (
        <div 
          className="video-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            transition: 'background-color 0.3s ease'
          }}
        >
          <div 
            className="play-button"
            style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: categoryColor,
              fontSize: '2rem',
              transition: 'all 0.3s ease',
              transform: isHovering ? 'scale(1.1)' : 'scale(1)'
            }}
          >
            <i className="bi bi-play-fill"></i>
          </div>
        </div>
      )}

      {(isPlaying || showControls || isHovering) && (
        <div 
          className="video-controls"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            padding: '10px',
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
            color: '#fff',
            transition: 'opacity 0.3s ease',
            opacity: isHovering || isPlaying ? 1 : 0
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="progress-bar-container"
            style={{
              width: '100%',
              height: '4px',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '2px',
              marginBottom: '10px',
              cursor: 'pointer'
            }}
            onClick={handleProgressClick}
          >
            <div 
              className="progress-bar"
              style={{
                width: `${progressPercentage}%`,
                height: '100%',
                backgroundColor: categoryColor,
                borderRadius: '2px',
                transition: 'width 0.1s linear'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={togglePlay}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
              </button>

              <button
                onClick={toggleMute}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '5px',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              >
                <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`}></i>
              </button>

              <span style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>
                {formatCurrentTime()} / {formatDuration(duration)}
              </span>
            </div>

            {isPlaying && (
              <span style={{
                fontSize: '0.8rem',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '2px 8px',
                borderRadius: '4px'
              }}>
                <i className="bi bi-play-circle me-1"></i>
                En lecture
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Composant StatisticsCards pour afficher les statistiques (style amélioré)
const StatisticsCards = ({ stats }) => {
  return (
    <div className="row mb-4">
      <div className="col-xl-4 col-md-4">
        <div className="card" style={{ 
          borderRadius: '12px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-white mb-1" style={{ fontWeight: '500', opacity: 0.9 }}>Total tutoriels</h6>
                <h2 className="text-white mb-0">{stats.total || 0}</h2>
                <small className="text-white" style={{ opacity: 0.8, fontSize: '0.85rem' }}>
                  Tous tutoriels disponibles
                </small>
              </div>
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                width: '50px',
                height: '50px',
                backgroundColor: 'rgba(255,255,255,0.2)'
              }}>
                <i className="bi bi-collection-play fs-4 text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-4 col-md-4">
        <div className="card" style={{ 
          borderRadius: '12px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          backgroundColor: COLORS.white
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1" style={{ fontWeight: '500' }}>Contrôleur</h6>
                <h2 className="mb-0" style={{ color: '#1976d2' }}>{stats.parProfile?.['2'] || 0}</h2>
                <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Pour contrôleurs
                </small>
              </div>
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                width: '50px',
                height: '50px',
                backgroundColor: '#e3f2fd'
              }}>
                <i className="bi bi-person-check fs-4" style={{ color: '#1976d2' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-4 col-md-4">
        <div className="card" style={{ 
          borderRadius: '12px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          backgroundColor: COLORS.white
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1" style={{ fontWeight: '500' }}>Gestionnaire</h6>
                <h2 className="mb-0" style={{ color: '#388e3c' }}>{stats.parProfile?.['3'] || 0}</h2>
                <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                  Pour gestionnaires
                </small>
              </div>
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                width: '50px',
                height: '50px',
                backgroundColor: '#e8f5e9'
              }}>
                <i className="bi bi-person-gear fs-4" style={{ color: '#388e3c' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TutorialList = () => {
  const navigate = useNavigate();
  
  const [tutos, setTutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProfile, setFilterProfile] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [statistiques, setStatistiques] = useState({});

  const fetchTutos = useCallback(async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        // Pour les utilisateurs non connectés, ne pas envoyer de token
        const params = new URLSearchParams();
        if (searchTerm.trim()) params.append("search", searchTerm.trim());
        if (filterProfile) params.append("profile", filterProfile);
        params.append("page", currentPage);
        params.append("limit", itemsPerPage);
        
        const url = `${API_BASE_URL}/tuto/index.php?${params.toString()}`;
        
        const response = await axios.get(url, {
          timeout: 10000,
        });
        
        handleResponse(response);
      } else {
        // Pour les utilisateurs connectés, envoyer le token
        const params = new URLSearchParams();
        if (searchTerm.trim()) params.append("search", searchTerm.trim());
        if (filterProfile) params.append("profile", filterProfile);
        params.append("page", currentPage);
        params.append("limit", itemsPerPage);
        
        const url = `${API_BASE_URL}/tuto/index.php?${params.toString()}`;
        
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        });
        
        handleResponse(response);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError("Erreur lors du chargement des tutoriels.");
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterProfile, currentPage, itemsPerPage, navigate]);

  const handleResponse = (response) => {
    if (response.data?.success) {
      const tutosData = response.data.data.tutos || [];
      setTutos(tutosData);
      
      const pagination = response.data.data.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalItems(pagination.total || 0);
      
      // Récupérer les statistiques
      if (response.data.data.profile_statistiques) {
        const stats = {
          total: pagination.total || 0,
          parProfile: {}
        };
        
        Object.entries(response.data.data.profile_statistiques).forEach(([profile, data]) => {
          stats.parProfile[profile] = data.total || 0;
        });
        
        setStatistiques(stats);
      } else {
        // Calculer les statistiques si non fournies par l'API
        const stats = {
          total: pagination.total || 0,
          parProfile: {}
        };
        
        // Compter par profile
        PROFILE_OPTIONS.forEach(prof => {
          if (prof.value) {
            stats.parProfile[prof.value] = tutosData.filter(t => t.profile_video === prof.value).length;
          }
        });
        
        setStatistiques(stats);
      }
      
      if (tutosData.length === 0) {
        if (searchTerm || filterProfile) {
          setError("Aucun tutoriel ne correspond aux critères de recherche.");
        } else {
          setError("Aucun tutoriel disponible pour le moment.");
        }
      }
    } else {
      setError(response.data?.message || "Impossible de charger la liste des tutoriels.");
    }
  };

  useEffect(() => {
    // Vérifier le profil utilisateur
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setCurrentProfile(String(userData.profile || "").trim());
      } catch (error) {
        console.error("Error parsing user:", error);
      }
    }
    
    fetchTutos();
  }, [fetchTutos]);

  const handleViewDetails = (tuto) => {
    navigate(`/tutos/details/${tuto.id_video}`);
  };

  const handleResetSearch = () => {
    setSearchTerm("");
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterProfile("");
    setCurrentPage(1);
  };

  const hasActiveFilters = () => {
    return searchTerm || filterProfile;
  };

  // Gérer le play/pause des vidéos
  const handleVideoPlay = (videoId) => {
    if (playingVideoId && playingVideoId !== videoId) {
      // Arrêter l'autre vidéo si elle est en cours
    }
    setPlayingVideoId(videoId);
  };

  const handleVideoPause = () => {
    setPlayingVideoId(null);
  };

  // Pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Lien pour les admins
  const handleGoToAdmin = () => {
    navigate("/tutos/admin");
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Tutoriels vidéo</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Tutoriels</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-collection-play"></i>
                    </div>
                    Catalogue des tutoriels
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Consultez les tutoriels vidéos de formation et procédures selon votre profil
                </p>
              </div>
              
              <div className="d-flex gap-2">
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold"
                    onClick={handleResetFilters}
                    title="Réinitialiser tous les filtres"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                )}
                
                {/* Bouton pour les administrateurs */}
                {currentProfile === "1" && (
                  <button
                    className="btn fw-semibold"
                    onClick={handleGoToAdmin}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="bi bi-shield-check me-2"></i>
                    Mode admin
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: '#f44336',
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid #f44336`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
            )}

            {/* Cartes de statistiques */}
            <StatisticsCards stats={statistiques} />

            {/* Filtres - Même style que TutorialAdminList */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher (titre, description)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                          {searchTerm && (
                            <button
                              className="btn"
                              type="button"
                              onClick={handleResetSearch}
                              title="Réinitialiser la recherche"
                              style={{
                                color: COLORS.primary,
                                borderColor: '#e0e0e0',
                                backgroundColor: COLORS.white,
                                borderLeft: 'none'
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterProfile}
                            onChange={(e) => setFilterProfile(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {PROFILE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-2">
                        <button
                          className="btn fw-semibold w-100"
                          onClick={handleApplyFilters}
                          style={{
                            color: COLORS.white,
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                            borderRadius: '8px',
                            padding: '10px'
                          }}
                        >
                          <i className="bi bi-funnel-fill me-2"></i>
                          Appliquer
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {totalItems} tutoriel(s) disponible(s)
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Filtres actifs:
                            {searchTerm && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Recherche</span>}
                            {filterProfile && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Profile</span>}
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={fetchTutos}
                        className="btn fw-semibold"
                        disabled={loading}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '6px 16px',
                          fontSize: '0.85rem'
                        }}
                      >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''} me-1`}></i>
                        {loading ? 'Chargement...' : 'Actualiser'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des tutoriels...</p>
              </div>
            ) : tutos.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-collection-play fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun tutoriel trouvé</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  {error || "Aucun tutoriel n'est disponible pour le moment."}
                </p>
              </div>
            ) : (
              <>
                <div className="row g-4">
                  {tutos.map((tuto) => {
                    const profileInfo = getProfileInfo(tuto.profile_video);
                    const profileColor = getProfileColor(tuto.profile_video);
                    
                    const videoId = `video-${tuto.id_video}`;
                    const isPlaying = playingVideoId === videoId;

                    return (
                     <div className="col-lg-6 col-md-6">
  <div className="card h-100" style={{ 
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    backgroundColor: COLORS.white,
    display: 'flex',
    flexDirection: 'column'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}>
    {/* En-tête */}
    <div className="card-header border-0 pb-0" style={{ 
      backgroundColor: profileColor,
      color: COLORS.white,
      padding: '12px 16px'
    }}>
      <div style={{ flex: 1 }}>
        <h5 className="card-title mb-2" style={{ 
          color: COLORS.white,
          fontSize: '0.95rem',
          fontWeight: '600',
          lineHeight: '1.3'
        }}>
          {tuto.titre_video}
        </h5>
        <div className="d-flex align-items-center">
          <i className={`bi ${profileInfo.icon} me-2`}></i>
          <span className="badge fw-semibold" style={{ 
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: COLORS.white,
            fontSize: '0.75rem',
            padding: '3px 8px',
            borderRadius: '4px'
          }}>
            {profileInfo.label}
          </span>
        </div>
      </div>
    </div>
    
    {/* Section vidéo */}
    <div style={{ padding: '0' }}>
      <VideoPlayer
        videoUrl={tuto.video_url_complete}
        duration={tuto.duree_video}
        videoId={videoId}
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        isPlaying={isPlaying}
        categoryColor={profileColor}
      />
    </div>
    
    {/* Corps de la carte */}
    <div className="card-body" style={{ 
      padding: '12px 16px',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Date et heure */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
          <i className="bi bi-calendar3 me-1"></i>
          {formatDateTime(tuto.date_publication || tuto.created_at)}
        </small>
        <small className="text-muted d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
          <i className="bi bi-clock me-1"></i>
          {formatDuration(tuto.duree_video)}
        </small>
      </div>
      
      {/* Description */}
      <div className="mb-3" style={{ 
        flexGrow: 1,
        minHeight: '70px'
      }}>
        <p className="card-text" style={{ 
          color: COLORS.darkText,
          fontSize: '0.85rem',
          lineHeight: '1.4',
          marginBottom: '8px'
        }}>
          {tuto.description_video ? 
            (tuto.description_video.length > 120
              ? tuto.description_video.substring(0, 120) + '...' 
              : tuto.description_video)
            : "Pas de description disponible."}
        </p>
      </div>
      
      {/* Bouton détail - REMONTÉ de -80px */}
      <div style={{ marginTop: '-80px' }}>
        <button
          className="btn fw-semibold w-100"
          onClick={() => handleViewDetails(tuto)}
          title="Voir les détails"
          style={{
            color: COLORS.white,
            backgroundColor: profileColor,
            borderColor: profileColor,
            borderRadius: '6px',
            padding: '8px 12px',
            transition: 'all 0.3s ease',
            fontSize: '0.85rem',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = COLORS.secondary;
            e.target.style.borderColor = COLORS.secondary;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = profileColor;
            e.target.style.borderColor = profileColor;
          }}
        >
          <i className="bi bi-eye me-1"></i>
          Voir les détails
        </button>
      </div>
    </div>
  </div>
</div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="d-flex align-items-center">
                      <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                      <select 
                        className="form-select form-select-sm w-auto"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        style={{
                          borderColor: '#e0e0e0',
                          color: COLORS.darkText,
                          borderRadius: '6px'
                        }}
                      >
                        <option value="12">12</option>
                        <option value="24">24</option>
                        <option value="36">36</option>
                        <option value="48">48</option>
                      </select>
                      <span className="ms-3" style={{ color: COLORS.darkText }}>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {totalItems} tutoriel(s) disponible(s)
                        </span>
                        <span className="text-muted small">
                          Page {currentPage} sur {totalPages}
                        </span>
                      </span>
                    </div>
                    
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link fw-semibold"
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            style={{
                              color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                              borderColor: '#e0e0e0',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              margin: '0 2px'
                            }}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            if (totalPages <= 7) return true;
                            return page === 1 || 
                                   page === totalPages || 
                                   (page >= currentPage - 1 && page <= currentPage + 1);
                          })
                          .map((page, index, array) => {
                            if (index > 0 && page > array[index - 1] + 1) {
                              return (
                                <React.Fragment key={`ellipsis-${page}`}>
                                  <li className="page-item disabled">
                                    <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                  </li>
                                  <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px'
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                </React.Fragment>
                              );
                            }
                            
                            return (
                              <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button 
                                  className="page-link fw-semibold"
                                  onClick={() => handlePageChange(page)}
                                  style={{
                                    backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                    borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                    color: currentPage === page ? COLORS.white : COLORS.primary,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    margin: '0 2px'
                                  }}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          })}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link fw-semibold"
                            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            style={{
                              color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                              borderColor: '#e0e0e0',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              margin: '0 2px'
                            }}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .bi-arrow-clockwise.spin {
          animation: spin 1s linear infinite;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
          
          .col-lg-4, .col-md-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
        }
        
        @media (max-width: 576px) {
          .card-header h5 {
            font-size: 0.95rem;
          }
          
          .card-body .row .col-6 {
            padding: 0 4px;
          }
          
          .card-footer .btn {
            font-size: 0.8rem !important;
            padding: 6px 8px !important;
          }
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </Layout>
  );
};

export default TutorialList;